import axios from "axios";
import * as Yup from "yup";
import {ModalContext} from "app/app";
import {Fragment, useContext, useState} from 'react';
import {Form, Formik} from 'formik';
import {toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {FieldInput} from "components/fieldInput";
import {useNavigate, ViewContext, useAPI} from "components/lib";
import styles from "./styles.module.scss";
import {useEffect} from "react";
import {Spinner} from "../../spinner/spinner";
import {noFeeAgencies, Providers} from "../../../helpers/constants";
import QuoteUtils from "../../../helpers/quoteUtils";
import DateUtils from "../../../helpers/dateUtils";

export function BindRequestModal() {
  const context = useContext(ViewContext);
  const navigate = useNavigate();
  const {modal, setModal} = useContext(ModalContext);
  const quoteUtils = new QuoteUtils();
  const dateUtils = new DateUtils();
  const user = useAPI('/api/user');
  


  const validationSchema = Yup.object().shape({
    quote: Yup.string().required("This field is required!"),
    submission_number: Yup.string()
      .required("This field is required!")
      .test("checkSubmissionNumber", "Submission number does not exist", async (value) => {
        if (!value) return false;
        try {
          const response = await axios.get("/api/quote/admin");
          const submissionNumbers = response.data.data.data.submission_number;
          return submissionNumbers.includes(value);
        } catch (error) {
          console.error("Failed to validate submission number", error);
          return false;
        }
      }),
    agency: Yup.string().when("quote", {
      is: "Progressive",
      then: Yup.string().required("This field is required!"),
    }),
    policy_number: Yup.string().when("quote", {
      is: "Progressive",
      then: Yup.string().required("This field is required!"),
    }),
    contactName: Yup.string().required("Contact name is required!"),
    contactEmail: Yup.string().email("Invalid email format").required("Contact email is required!"),
    contactPhone: Yup.string().required("Contact phone is required!"),
  });

  const quoteType = ["Cover Whale", "Progressive", "Nirvana"];
  const [selectedQuote, setSelectedQuote] = useState("");
  const [agenciesOptions, setAgenciesOptions] = useState([]);
  const [quoteNumberValue, setQuoteNumberValue] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [agencyNames, setAgencyNames] = useState({});
  const [loading, setLoading] = useState(false);

  /*const fetchQuoteData = async () => {
    try {
      const response = await axios.get("/api/quote/admin");
      const data = response.data.data.data;

      setAgenciesOptions(
        data.accounts.map((agency) => ({
          label: agency.name,
          value: agency.id,
        }))
      );
      const agencyNames = data.accounts.reduce((acc, agency) => {
        acc[agency.id] = agency.name;
        return acc;
      }, {});
      setAgencyNames(agencyNames);
    } catch (error) {
      console.error(error);
      context.notification.show(
        "Failed to load quote",
        "error",
        true,
        "toast",
        "x-circle"
      );
    }
  }*/

  const fetchAllAgencies = async () => {
    try {
      const response = await axios.get("/api/account/all/minimal");


      const agencies = response.data.data;
      const sortedAgencies = agencies
        .map((agency) => ({
          label: agency.name.trim(),
          value: agency.id,
        }))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

      setAgenciesOptions(sortedAgencies);

      const agencyNames = agencies.reduce((acc, agency) => {
        acc[agency.id] = agency.name;
        return acc;
      }, {});

      setAgencyNames(agencyNames);
    } catch (error) {
      console.error("Error fetching all accounts:", error);
      context.notification.show(
        "Failed to load agencies",
        "error",
        true,
        "toast",
        "x-circle"
      );
    }
  };

  useEffect(() => {
    fetchAllAgencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [agencyInfo, setAgencyInfo] = useState({
    agencyId: "",
    agencyName: "",
  });

  const handleAgencyChange = (agency) => {
    const agencyId = Array.isArray(agency) ? agency[0].value : agency.value;

    setAgencyInfo({
      agencyId: agencyId,
      agencyName: agencyNames[agencyId],
    });
  };

  const handleError = (error, contextMessage = "An error occurred") => {
    const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error.message || "Unexpected error";

    toast.error(`${contextMessage}: ${errorMessage}`);
    console.error(`${contextMessage}:`, error);
  };

  const createBindRequest = async () => {
    try {
      setLoading(true);

      const {data} = await axios.get(`/api/account/${agencyInfo.agencyId}`);
      const agentEmail = data.data.owner_email;

      let bindReqData = {
        status: "Active",
        agencyId: agencyInfo.agencyId,
        companyName: companyName,
        agencyName: agencyInfo.agencyName,
        assignedAgentEmail: agentEmail,
        submissionNumber: quoteNumberValue,
        quote: selectedQuote,
        userId: user?.data?.id || null, // Add userId for backend validation
      };



      if (quoteNumberValue && selectedQuote === "Cover Whale") {
        try {
          const response = await axios.get(`/api/quote/admin?page=1&search=${quoteNumberValue}`);
          const quotes = response.data.data.data.quotes;

          
          if (quotes.length === 0) {
            console.warn("No quotes found for submission number:", quoteNumberValue);
            // Continue without quote data - create basic bind request
            bindReqData.quoteId = null;
            bindReqData.effectiveDate = null;
            bindReqData.totalCost = 0;
          } else {
            let validQuote = quotes.find(quote => quote.company_name && quote.account_name);

          let quoteId = validQuote?.id;
          let effectiveDate = validQuote?.effective_date
            ? dateUtils.formatIsoToDateOnly(validQuote.effective_date)
            : null;
          let totalCost = 0;
          let fetchedQuote = null;

          try {
            const res = await axios.get(`/api/quote/info/${quoteNumberValue}`);
            fetchedQuote = res.data.data;
          } catch (error) {
            console.warn("No fetched quote found.");
          }

          if (validQuote && fetchedQuote) {
            quoteId = validQuote.id;
            effectiveDate = effectiveDate || dateUtils.formatIsoToDateOnly(fetchedQuote.coverages?.al?.policy_effective_date);

            const rawCoverages = {
              ...fetchedQuote,
              towingLaborStorage: {
                value: Number(validQuote?.quote?.limits?.limitTowingStorage) || 0,
                included: true,
              },
              waiverSubrogation: {
                included: validQuote?.quote?.limits?.insuredWaiverSubrogation === "Y",
              },
              UIIAintermodal: {
                included: validQuote?.quote?.limits?.UIIAintermodal === "Y",
              },
              primaryNoncontributoryAL: {
                included: validQuote?.quote?.limits?.primaryNoncontributoryAL === "Y",
              },
              primaryNoncontributoryTGL: {
                included: validQuote?.quote?.limits?.primaryNoncontributoryTGL === "Y",
              },
              trailerInterchange: {
                limit: validQuote?.quote?.limits?.trailerInterchangeLimit || 0,
                included: validQuote?.quote?.limits?.trailerInterchange === "Y",
              },
              commoditiesRefrigeration: {
                included: validQuote?.quote?.commoditiesRefrigeration === "Y",
              },
            };

            try {
              const {financialDetails} = quoteUtils.calculate(
                rawCoverages,
                Providers.CoverWhale,
                noFeeAgencies.includes(agencyInfo.agencyId)
              );

              totalCost = financialDetails?.totalCost ?? 0;


            } catch (error) {
              console.error("Error while calculating total cost:", error);
            }
          } else if (validQuote) {
            quoteId = validQuote.id;
            effectiveDate = dateUtils.formatIsoToDateOnly(validQuote.effective_date);
          } else if (fetchedQuote) {
            quoteId = fetchedQuote.id;
            effectiveDate = dateUtils.formatIsoToDateOnly(fetchedQuote.coverages?.al?.policy_effective_date);
          }

            bindReqData.quoteId = quoteId;
            bindReqData.effectiveDate = effectiveDate;
            bindReqData.totalCost = totalCost;
          }
        } catch (error) {
          console.warn("Error handling Cover Whale quote:", error);
        }
      } else if (quoteNumberValue && ["Progressive", "Nirvana"].includes(selectedQuote)) {
        try {
          let totalCost = 0;
          const res = await axios.get(`/api/quote/admin?page=1&search=${quoteNumberValue}`);
          const quotes = res.data.data.data.quotes;

          
          if (quotes.length === 0) {
            console.warn("No quotes found for submission number:", quoteNumberValue);
            // Continue without quote data - create basic bind request
            bindReqData.quoteId = null;
            bindReqData.effectiveDate = null;
            bindReqData.totalCost = 0;
          } else {
            const validQuote = quotes.find(quote => quote.company_name && quote.account_name);

            // Progressive API integration removed - only manual data entry
            if (selectedQuote === "Progressive") {

              totalCost = 0; // Will be manually entered
            }

            if (validQuote) {
              bindReqData.quoteId = validQuote.id;
              bindReqData.effectiveDate = dateUtils.formatIsoToDateOnly(validQuote.effective_date);
              bindReqData.totalCost = totalCost;
            } else {
              console.warn("No valid quote found for Progressive/Nirvana - creating with basic data");
              bindReqData.quoteId = null;
              bindReqData.effectiveDate = null;
              bindReqData.totalCost = totalCost || 0;
            }
          }
        } catch (error) {
          console.warn("Error fetching Progressive/Nirvana quote:", error);
        }
      }

      try {
        const response = await axios.post("/api/bind-requests/admin", bindReqData);
        if (response.status === 201) {
          const bindReqId = response.data.data.id;
          handleModalClose();
          navigate(`/admin/requestedbind/${bindReqId}`);
        }
      } catch (error) {
        handleError(error, "Error creating bind request");
      }
    } catch (error) {
      handleError(error, "Error creating bind request");
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setModal("");
    setLoading(false);
  };

  const handleSubmit = () => {
    return createBindRequest();
  }

  return (
    <Fragment>
      {modal.name === "bind_request_modal" && (
        <div className={styles.background}>
          <div className={styles.wrapper}>
            <div className={styles.insideWrapper}>
              <div
                style={{
                  fontStyle: "normal",
                  fontWeight: 500,
                  fontSize: 24,
                  marginBottom: 10,
                  color: "black",
                }}
              >
                New Bind Request
              </div>
              <Formik
                initialValues={{
                  submissionNumber: '',
                  agency: '',
                  companyName: '',
                }}
                validationSchema={validationSchema}
              >
                <>
                  <Form
                    className={styles.form}
                  >
                    <div
                      style={{
                        width: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "black",
                        marginTop: 20,
                        marginBottom: 20,
                      }}
                    >
                      {selectedQuote === "" && (
                        <>
                          <div
                            style={{
                              fontStyle: "normal",
                              fontWeight: 400,
                              fontSize: 18,
                              marginBottom: 10
                            }}
                          >
                            Create a bind request with quote from
                          </div>
                          {quoteType.map((quote, index) => (
                            <button
                              key={index}
                              style={{
                                width: "100%",
                                padding: "10px 40px",
                                backgroundColor: "white",
                                border: "solid 1px #ddd",
                                borderRadius: "6px",
                                marginBottom: "6px",
                                cursor: "pointer",
                                fontStyle: "normal",
                                fontWeight: 500,
                                fontSize: 18,
                              }}
                              onClick={() => setSelectedQuote(quote)}
                            >
                              {quote}
                            </button>
                          ))}
                        </>
                      )}

                      {selectedQuote === "Cover Whale" && (
                        <>
                          <div style={{marginTop: 5}}>
                            <FieldInput
                              id="company"
                              type="text"
                              label="Name of Insured"
                              placeholder="Enter insured company name..."
                              handleChange={(e) => {
                                setCompanyName(e.target.value);
                              }}
                              value={companyName}
                            />
                          </div>
                          <div style={{marginTop: 5}}>
                            <FieldInput
                              id="quote"
                              type="text"
                              label="Submission Number"
                              placeholder="Enter submission number..."
                              handleChange={
                                (e) => setQuoteNumberValue(e.target.value)
                              }
                              value={quoteNumberValue || ""}
                            />
                          </div>
                          <div style={{marginTop: 5}}>
                            <FieldInput
                              id="agency"
                              type="search_select"
                              label="Name of Retail Agency"
                              placeholder="Select agency..."
                              handleChange={handleAgencyChange}
                              options={agenciesOptions}
                            />
                          </div>
                        </>
                      )}
                      {(selectedQuote === "Progressive" || selectedQuote === "Nirvana") && (
                        <>
                          <div style={{marginTop: 5}}>
                            <FieldInput
                              id="company"
                              type="text"
                              label="Name of Insured"
                              placeholder="Enter insured company name..."
                              handleChange={(e) => {
                                setCompanyName(e.target.value);
                              }}
                              value={companyName}
                            />
                          </div>
                          <div style={{marginTop: 5}}>
                            <FieldInput
                              id="quote"
                              type="text"
                              label="Submission Number"
                              placeholder="Enter submission number..."
                              handleChange={
                                (e) => setQuoteNumberValue(e.target.value)
                              }
                              value={quoteNumberValue}
                            />
                          </div>
                          <div style={{marginTop: 5}}>
                            <FieldInput
                              id="agency_filter"
                              type="search_select"
                              label="Name of Retail Agency"
                              defaultValue={[]}
                              handleChange={handleAgencyChange}
                              options={agenciesOptions}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </Form>
                  <div
                    className={styles.footer}
                    style={{justifyContent: "flex-end",}}
                  >
                    <div className={styles.buttons}>
                      <button
                        className={styles.cancel}
                        onClick={() => {
                          setModal("");
                          setSelectedQuote("");
                        }}
                      >
                        Cancel
                      </button>

                      {selectedQuote !== "" && (
                        <button
                          className={styles.save}
                          type="button"
                          onClick={handleSubmit}
                          disabled={loading}
                        >
                          {loading ? 'Creating...' : 'Next'}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              </Formik>
            </div>
          </div>
        </div>
      )}
      <Spinner loading={loading}/>
    </Fragment>
  )
}